import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  uiHelpersState,
  selectBucketsRemainingCount,
} from "@/global/store/data/uiHelpers";
import { pluralize } from "@/global/utils/utils";
import {
  selectEvents,
  selectEventsSelected,
  updateEventsSelected,
  updateEventsSelectedWithoutSort,
} from "@/global/store/data/purchaseSeats/events";
import {
  bucketsState,
  selectBuckets,
} from "@/global/store/data/purchaseSeats/buckets";
import {
  cartManagerState,
  selectCartManager,
} from "@/global/store/data/purchaseSeats/cartManager";
import { getUpcomingEventsApi } from "@/global/api/events";
import { useAppDispatch, useAppStore } from "@/global/store";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

import type { EventType } from "@/global/store/data/types";

export function useBuckets() {
  const dispatch = useAppDispatch();
  const { sm } = useCssMediaQuery("down");
  const { buckets, languages, purchaseSeatConfigs, uiConfigs } =
    useTeamConfig();

  /**
   * States
   */
  const events = useAppStore(selectEvents);
  const cartState = useAppStore(selectCartManager);
  const availableBuckets = useAppStore(selectBuckets);
  const selectedEvents = useAppStore(selectEventsSelected);
  const isBucketsEnabled = (!!buckets && buckets.enabled) || false;
  const bucketsRemainingCount = useAppStore(selectBucketsRemainingCount);
  const isBucketsEnabledAndMobile = (sm && isBucketsEnabled) || false;
  const prioritizeMinGameSelectionCount = buckets?.prioritizeMinGameSelectionCount || false; // prettier-ignore
  const progressBarGameText = languages?.purchaseSeats?.progressBarGameText || "game"; // prettier-ignore
  const progressBarAddLabel = languages?.purchaseSeats?.progressBarAddLabel || "Add"; // prettier-ignore
  const progressBarAditionalText = languages?.purchaseSeats?.progressBarAditionalText || "to continue!"; // prettier-ignore

  /**
   * Mutations
   */
  const getEvents = useMutation(getUpcomingEventsApi);

  function handleResetBuckets(data?: any) {
    // prettier-ignore
    if (isBucketsEnabled && !!buckets) {
      const minId = Math.min(...buckets.list.map((itm) => itm.id)); // get the min id available
      const availableBucketEvents = buckets?.list.map((itm) => ({
        id: itm.id,
        title: itm.name,
        description: itm.description,
        opened: itm.id == minId,
        disabled: itm.id != minId,
        events: (data || events).filter(
          (event: EventType) => event.bucketId === itm.id
        ),
        min: buckets.list.find(b => b.id === itm.shouldUseMinAmountOfBucketId)?.min || itm.min,
        max: buckets.list.find(b => b.id === itm.shouldUseMaxAmountOfBucketId)?.max || itm.max,
        isMaxCountReached: false,
        remainingBucketLanguage: itm.remainingBucketLanguage,
      }));

      dispatch(bucketsState.actions.setBuckets(availableBucketEvents)); // prettier-ignore
    }
  }

  /**
   * Function to handle event selection
   * @param event
   */
  // prettier-ignore
  function handleEventSelection(event: EventType) {
    if (uiConfigs?.findGames?.setEventsWithoutSorting) {
      dispatch(updateEventsSelectedWithoutSort(event));
    } else dispatch(updateEventsSelected(event));

    if (cartState.cart.some((itm) => itm.eventCode == event.eventCode)) {
      dispatch(cartManagerState.actions.removeEvent({ eventCode: event.eventCode }));
    }
  }

  /**
   * Function to get updated min quantity required for bucket based on reducer
   * @param bucketId
   */
  // prettier-ignore
  function handleGetBucketUpdatedMinMax(bucketId: number): { min: number; max: number } {
    return availableBuckets.reduce((acc, bucket) => {
      if (bucket.id === bucketId) {
        return {
          min: bucket.min,
          max: bucket.max,
        };
      }
      return acc;
    }, { min: 0, max: 0 });
  }

  function handleGetNextBucketById(bucketId: number) {
    if (!buckets) return;
    const nextBucket = buckets.list.find((bucket) => bucket.id === bucketId + 1); // prettier-ignore
    if (nextBucket) return nextBucket;
  }

  /**
   * Function to select event if buckets are enabled
   * @param event EventType
   * @param bucketId bucket id
   */
  // prettier-ignore
  function handleSelectEvent(event: EventType, bucketId: number) {
    if (!isBucketsEnabled || !buckets) return;

    handleEventSelection(event);

    const selectedEventsQuantity: number = [...selectedEvents, event].filter(
      (event) => event.bucketId === bucketId
    ).length;

    const bucketQuantity = handleGetBucketUpdatedMinMax(bucketId);

    const dontCloseWhenHitMin = buckets.list.find((bucket) => bucket.id === bucketId)?.dontCloseWhenHitMin || false;
    const bucket = buckets.list.find((bucket) => bucket.id === bucketId);
    if (selectedEventsQuantity == bucketQuantity.min) {
      if (!dontCloseWhenHitMin) handleOpenBucket(bucketId);
      if (bucket?.scrollToTopWhenSelecting && !bucket?.disableScrollToTopWhenSelectingMin) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }

    // if selected events is equal to bucket max, it means the bucket is full and we need to disable it
    if (selectedEventsQuantity == bucketQuantity.max) {
      dispatch(bucketsState.actions.setBucketMaxCountReached({ id: bucketId, isMaxCountReached: true }));
      if (bucket?.closeWhenHitMax) {
        handleOpenBucket(bucketId);
      }
      if (bucket?.scrollToTopWhenSelecting) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }

    // if values are the same, it means the next bucket should get enabled - ok
    const nextBucket = handleGetNextBucketById(bucketId);
    if (selectedEventsQuantity == bucketQuantity.min) {
      if (nextBucket)
        dispatch(
          bucketsState.actions.updateOpenedBucket({
            opened: true,
            disabled: false,
            id: nextBucket.id,
          })
        );
    }

    // finding next bucket, checking if it has shouldUseMaxAmountOfBucketId and update max property
    if (nextBucket && nextBucket.shouldUseMaxAmountOfBucketId == bucketId) {
      const { payload } = dispatch(bucketsState.actions.updateBucketMax({ id: nextBucket.id, max: selectedEventsQuantity }));
      const selectedEventsInNextBucket = selectedEvents.filter((events) => events.bucketId === nextBucket.id).length;
      if (payload.max > selectedEventsInNextBucket) {
        dispatch(bucketsState.actions.setBucketMaxCountReached({ id: nextBucket.id, isMaxCountReached: false }));
      }
    }
  }

  /**
   * Function to deselect event if buckets are enabled
   * @param event EventType
   * @param bucketId bucket id
   */
  // prettier-ignore
  function handleDeselectEvent(event: EventType, bucketId: number) {
    if (!isBucketsEnabled || !buckets) return;

    handleEventSelection(event);

    const selectedEventsQuantity: number = selectedEvents.filter((selectedEvent) =>
      selectedEvent.bucketId === bucketId &&
      selectedEvent.eventCode != event.eventCode
    ).length;

    const bucketQuantity = handleGetBucketUpdatedMinMax(bucketId);

    if (selectedEventsQuantity < bucketQuantity.min) {
      // loop into subsequent buckets and disable them - ok
      const sortedBucketIds = buckets.list.sort((a, b) => a.id - b.id);
      for (let i = bucketId; i < sortedBucketIds.length; i++) {
        const bucket = buckets.list[i];
        dispatch(bucketsState.actions.updateOpenedBucket({ id: bucket.id, opened: false, disabled: true }));
        dispatch(bucketsState.actions.setBucketMaxCountReached({ id: bucket.id, isMaxCountReached: false }));
        // unselect events from subsequent buckets
        selectedEvents.forEach((selectedEvent) => {
          if (selectedEvent.bucketId === bucket.id) handleEventSelection(selectedEvent);
        });
      }
    }

    // if selected events quantity is less than bucket max, enable the bucket again
    if (selectedEventsQuantity < bucketQuantity.max)
      dispatch(bucketsState.actions.setBucketMaxCountReached({ id: bucketId, isMaxCountReached: false }));

    const nextBucket = handleGetNextBucketById(bucketId);
    if (nextBucket && nextBucket.shouldUseMaxAmountOfBucketId == bucketId && selectedEventsQuantity > 0) {
      const { payload } = dispatch(bucketsState.actions.updateBucketMax({ id: nextBucket.id, max: selectedEventsQuantity }));
      // if selected events quantity in next bucket is greater max property, we need to remove one event
      const selectedEventsInNextBucket = selectedEvents.filter((selectedEvent) => selectedEvent.bucketId === nextBucket.id);
      if (selectedEventsInNextBucket.length > payload.max) {
        const eventToRemove = selectedEventsInNextBucket[selectedEventsInNextBucket.length - 1];
        if (eventToRemove) {
          handleEventSelection(eventToRemove);
          dispatch(bucketsState.actions.setBucketMaxCountReached({ id: nextBucket.id, isMaxCountReached: true }));
        } 
      } else if (payload.max == selectedEventsInNextBucket.length) {
        dispatch(bucketsState.actions.setBucketMaxCountReached({ id: nextBucket.id, isMaxCountReached: true }));
      }
    }
  }

  /**
   * Function to open bucket when clicking on it
   * @param bucketId
   */
  // prettier-ignore
  function handleOpenBucket(bucketId: number) {
    if (!buckets) return;
    if (availableBuckets.some((itm) => itm.id == bucketId && itm.opened)) {
      return dispatch(bucketsState.actions.updateOpenedBucket({ id: bucketId, opened: false, disabled: false }));
    }
    dispatch(bucketsState.actions.updateOpenedBucket({ id: bucketId, opened: true, disabled: false }));
  }

  /**
   * Function responsible to handle event selection remaining count based on bucket id
   */
  // prettier-ignore
  function handleEventSelectionRemainingCount() {
    const remainingCount = availableBuckets.map((bucket) => {
      const selectedEventsQuantity = selectedEvents.filter((event) => event.bucketId === bucket.id).length;
      const bucketQuantity = handleGetBucketUpdatedMinMax(bucket.id as number);
      const remaining = bucketQuantity.min - selectedEventsQuantity;
      return {
        remaining,
        id: bucket.id as number,
        isComplete: remaining <= 0,
      };
    });
  
    dispatch(uiHelpersState.actions.setBucketRemainingCount(remainingCount));
  }

  // prettier-ignore
  function getSelectedEventsPerBucket(): number {
    if (!buckets) return 0;
    return buckets.list.reduce((acc, bucket) => {
      const selectedEventsQuantity = selectedEvents.filter((event) => event.bucketId === bucket.id).length;
      return acc + (selectedEventsQuantity > bucket.min ? bucket.min : selectedEventsQuantity);
    }, 0);
  }

  // prettier-ignore
  function getBucketMessage(): string {
    if (prioritizeMinGameSelectionCount) {
      const remaningGameCount = purchaseSeatConfigs.minGameSelectionCount - selectedEvents.length;
      return `${progressBarAddLabel} ${remaningGameCount} ${pluralize(progressBarGameText, remaningGameCount)} ${progressBarAditionalText}`;
    }

    const remainingBuckets = bucketsRemainingCount.find((bucket) => !bucket.isComplete);
    if (remainingBuckets) {
      const { id, remaining } = remainingBuckets;
      const bucketRemainingLanguage = (availableBuckets.find((bucket) => bucket.id === id)?.remainingBucketLanguage);
      return bucketRemainingLanguage || `${progressBarAddLabel} ${remaining} more ${pluralize(progressBarGameText, remaining)} ${progressBarAditionalText}`;
    }
    return ""; // No open buckets
  }

  // prettier-ignore
  function getBucketStaticMin(): number {
    if (!buckets) return 0;
    return buckets.list.reduce((acc, bucket) => {
      if (bucket.shouldUseMinAmountOfBucketId) {
        const bucketMin = buckets.list.find((itm) => itm.id == bucket.shouldUseMinAmountOfBucketId)?.min;
        return acc + (bucketMin || 0);
      }
      return acc + bucket.min;
    }, 0);
  }

  function isPrimaryBucketsSelected(): boolean {
    if (!buckets) return false;
    const primaryBucket = buckets.list.find((bucket) => bucket.isPrimary);
    if (!primaryBucket) return false;
    // prettier-ignore
    const selectedEventsQuantityOnPrimaryBucket = selectedEvents.filter((event) =>
      event.bucketId === primaryBucket.id
    ).length;
    return selectedEventsQuantityOnPrimaryBucket <= primaryBucket.min;
  }

  const [bucketMessage, setBucketMessage] = useState(getBucketMessage());
  const [selectedEventsPerBucket, setSelectEventsPerBucket] = useState(getSelectedEventsPerBucket()); // prettier-ignore
  const [bucketMin, setBucketMin] = useState(getBucketStaticMin());

  useEffect(() => {
    if (isBucketsEnabled) {
      setBucketMin(getBucketStaticMin());
      setBucketMessage(getBucketMessage());
      setSelectEventsPerBucket(getSelectedEventsPerBucket());
    }
  }, [isBucketsEnabled, bucketsRemainingCount]);

  useEffect(() => {
    if (isBucketsEnabled) handleEventSelectionRemainingCount();
  }, [isBucketsEnabled, selectedEvents, availableBuckets]);

  return {
    buckets,
    bucketMin,
    bucketMessage,
    handleOpenBucket,
    availableBuckets,
    isBucketsEnabled,
    handleSelectEvent,
    handleDeselectEvent,
    bucketsRemainingCount,
    selectedEventsPerBucket,
    isBucketsEnabledAndMobile,
    prioritizeMinGameSelectionCount,
    isPrimaryBucketsSelected: isPrimaryBucketsSelected(),
  };
}
